/**
 * QualitativeReviewSection
 *
 * Qualitative Review capture section inside the TradeDrawer.
 * Stateless for schema fields (value/onChange), stateful for Additional Comments
 * which are stored as a single NoteDraft (noteType='trade', tradePhase='review').
 *
 * Layout:
 *   ── Lessons Learned ─────────────────────────
 *   [textarea]
 *
 *   ── Take again? ─────────────────────────────
 *   [  Yes  ] [  Maybe  ] [  No  ]
 *
 *   ── Additional Comments ─────────────────────
 *   [textarea — auto-saves as a single note on change]
 *
 * The 3-option toggle stores 'Yes' | 'Maybe' | 'No'.
 * Clicking the active option toggles it off (deselects).
 * Additional Comments auto-save as a single NoteDraft (tradePhase='review').
 *
 * Note: This section's props use locally-defined types. It will be rewired to
 * write to TradeService / Note entities in T08-T09 (persistent-trade mode).
 * @see noteDraftService — Additional Comments persistence (current draft-mode impl)
 */

// 1. External dependencies
import React, { useState, useEffect, useRef } from 'react';
import { ThumbsUp, Minus, ThumbsDown } from 'lucide-react';

// 2. Internal absolute imports
import { TextArea, Divider } from '@enfomentis/ui';
import { noteDraftService } from '@/services/NoteDraftService';

// ===== TYPES =====

/** Qualitative review state captured by this section. Local type — not stored on draft layer. */
export interface QualitativeReviewSectionValue {
  lessonsLearned?: string;
  /** Would the trader take this trade again? */
  wouldRepeat?: 'Yes' | 'Maybe' | 'No';
}

type QualReview = QualitativeReviewSectionValue;
type WouldRepeat = NonNullable<QualReview['wouldRepeat']>;

export interface QualitativeReviewSectionProps {
  value: QualitativeReviewSectionValue | undefined;
  onChange: (updated: QualReview) => void;
  /** Trade draft ID — used as ownerId for the Additional Comments note draft */
  draftId: string;
}

// ===== CONSTANTS =====

const WOULD_REPEAT_OPTIONS: Array<{
  value: WouldRepeat;
  label: string;
  description: string;
  Icon: React.ElementType;
}> = [
  { value: 'Yes',   label: 'Yes',   description: 'Good setup, would take again', Icon: ThumbsUp   },
  { value: 'Maybe', label: 'Maybe', description: 'With modifications',            Icon: Minus      },
  { value: 'No',    label: 'No',    description: 'Would avoid this setup',        Icon: ThumbsDown },
];

const AUTOSAVE_DELAY_MS = 1000;

// ===== COMPONENT =====

export const QualitativeReviewSection: React.FC<QualitativeReviewSectionProps> = ({
  value,
  onChange,
  draftId,
}) => {
  const cur: QualReview = value ?? {};

  // ---------------------------------------------------------------------------
  // Additional Comments — single NoteDraft (tradePhase='review')
  // ---------------------------------------------------------------------------

  const [additionalComments, setAdditionalComments] = useState('');
  /** ID of the existing note draft, if one has been created */
  const commentNoteIdRef = useRef<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing note text on draftId change
  useEffect(() => {
    let mounted = true;
    setAdditionalComments('');
    commentNoteIdRef.current = null;
    if (!draftId) return;
    void noteDraftService.getByOwner('trade', draftId).then((notes) => {
      if (!mounted) return;
      const existing = notes.find((n) => n.tradePhase === 'review');
      if (existing) {
        setAdditionalComments(existing.text);
        commentNoteIdRef.current = existing.id;
      }
    });
    return () => { mounted = false; };
  }, [draftId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current !== null) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const handleAdditionalComments = (val: string): void => {
    setAdditionalComments(val);

    if (autoSaveTimerRef.current !== null) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (!draftId) return;

      void (async () => {
        const text = val.trim();
        const existingId = commentNoteIdRef.current;

        if (existingId) {
          if (text) {
            await noteDraftService.update(existingId, {
              text,
              updatedAt: new Date().toISOString(),
            });
          } else {
            await noteDraftService.softDelete(existingId);
            commentNoteIdRef.current = null;
          }
        } else if (text) {
          const note = await noteDraftService.add({
            noteType: 'trade',
            captureContext: 'live-trading',
            ownerId: draftId,
            tradePhase: 'review',
            text,
            timestamp: new Date().toISOString(),
          });
          commentNoteIdRef.current = note.id;
        }
      })();
    }, AUTOSAVE_DELAY_MS);
  };

  // ---------------------------------------------------------------------------
  // Schema field handlers (exactOptionalPropertyTypes safe)
  // ---------------------------------------------------------------------------

  const handleLessonsLearned = (val: string): void => {
    if (val.trim()) {
      onChange({ ...cur, lessonsLearned: val });
    } else {
      const { lessonsLearned: _ll, ...rest } = cur;
      onChange(rest);
    }
  };

  const handleWouldRepeat = (option: WouldRepeat): void => {
    if (cur.wouldRepeat === option) {
      const { wouldRepeat: _wr, ...rest } = cur;
      onChange(rest);
    } else {
      onChange({ ...cur, wouldRepeat: option });
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3">

      {/* ── Lessons Learned ───────────────────────── */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide whitespace-nowrap">
          Lessons Learned
        </span>
        <Divider />
      </div>

      <TextArea
        placeholder="What did this trade teach you?"
        value={cur.lessonsLearned ?? ''}
        onValueChange={handleLessonsLearned}
        size="sm"
        variant="bordered"
        minRows={2}
        maxRows={5}
      />

      {/* ── Take again? ───────────────────────────── */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide whitespace-nowrap">
          Take again?
        </span>
        <Divider />
      </div>

      <div className="grid grid-cols-3 gap-2">
        {WOULD_REPEAT_OPTIONS.map((opt) => {
          const isActive = cur.wouldRepeat === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleWouldRepeat(opt.value)}
              className={[
                'flex flex-col items-center gap-1.5 rounded-lg border px-2 py-2 text-center transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-default-300 bg-transparent text-text-secondary hover:border-default-400 hover:bg-default-100',
              ].join(' ')}
            >
              <opt.Icon size={14} />
              <span className="text-xs font-semibold leading-none">{opt.label}</span>
              <span className="text-[10px] leading-tight text-text-tertiary">{opt.description}</span>
            </button>
          );
        })}
      </div>

      {/* ── Additional Comments ───────────────────── */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide whitespace-nowrap">
          Additional Comments
        </span>
        <Divider />
      </div>

      <TextArea
        placeholder="Any additional thoughts on this trade…"
        value={additionalComments}
        onValueChange={handleAdditionalComments}
        size="sm"
        variant="bordered"
        minRows={2}
        maxRows={5}
      />
    </div>
  );
};
